<template>
  <div class="content-area board-page noselect">
    <hero-bar>
      {{ $t('board') }}
      <b-tooltip slot="right" :label="$t('refresh')" type="is-dark" position="is-bottom">
        <b-button
          id="refresh-button"
          class="mr-10"
          size="is-circle"
          type="is-link"
          outlined
          :class="{ 'is-loading': refreshLoading }"
          icon-left="refresh"
          @click="refresh"
        />
      </b-tooltip>
      <b-tooltip slot="right" :label="$t('exportExcel')" type="is-dark" position="is-bottom">
        <b-button
          class="mr-10 is-hidden-mobile"
          size="is-circle"
          type="is-link"
          outlined
          :class="{ 'is-loading': excelLoading }"
          icon-left="file-excel"
          @click="exportExcel"
        />
      </b-tooltip>
    </hero-bar>

    <section class="section board-items is-main-section level">
      <card-component :has-button-slot="true" :title="$t('availableCars')" icon="car-multiple" class="is-block is-child level-item mr-10">
        <b-tag slot="button" class="count" type="available" rounded>{{ getCount('availableVehicles') }}</b-tag>
        <b-skeleton :active="pageLoading" :count="30" />
        <car-list :items="board.availableVehicles" />
      </card-component>

      <card-component :has-button-slot="true" :title="$t('availableDrivers')" icon="account-group" class="is-block is-child level-item mr-10">
        <b-tag slot="button" class="count" type="available" rounded>{{ getCount('availableDrivers') }}</b-tag>
        <b-skeleton :active="pageLoading" :count="30" />
        <driver-list :items="board.availableDrivers" />
      </card-component>

      <card-component :has-button-slot="true" :title="$t('plannedCars')" icon="car-key" class="is-block is-child level-item mr-10">
        <b-tag slot="button" class="count" type="wdeparture" rounded>{{ getCount('waitingForDepartureTransfers') }}</b-tag>
        <b-skeleton :active="pageLoading" :count="30" />
        <car-driver-list :items="board.waitingForDepartureTransfers" />
      </card-component>

      <card-component :has-button-slot="true" :title="$t('enrouteCars')" icon="road-variant" class="is-block is-child level-item mr-10">
        <b-tag slot="button" class="count" type="enroute" rounded>{{ getCount('enRouteTransfers') }}</b-tag>
        <b-skeleton :active="pageLoading" :count="30" />
        <car-driver-list :items="board.enRouteTransfers" />
      </card-component>

      <card-component :has-button-slot="true" :title="$t('completions')" icon="marker-check" class="is-block is-child level-item complate-transfers">
        <b-tag slot="button" class="count" type="completed" rounded>{{ getCount('completedTransfers') }}</b-tag>
        <b-skeleton :active="pageLoading" :count="30" />
        <car-driver-list :items="board.completedTransfers" :isCompleted="true" />
      </card-component>
    </section>
    <b-modal
      :active.sync="isTransferListModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
    >
      <div class="modal-card full-width" style="width: auto">
        <section class="modal-card-body full-width">
          <transfers-table
            id="transferTable"
            ref="transferTable"
            data-url="/list-transfers-fast"
            :isModal="true"
            :where="transferListWhere"
            :driverId="driverId"
            :carId="carId"
          />
        </section>
      </div>
    </b-modal>

    <b-modal
      :active.sync="isTransferModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
    >
      <div class="modal-card full-width" style="width: auto">
        <section class="modal-card-body full-width">
          <transfer-details
            :id="transferId"
            :isModal="true"
          />
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import download from 'downloadjs'

export default {
  name: 'Board',
  title: ({ $i18n }) => $i18n.t('titles.board'),
  components: {
    HeroBar: () => import('@/components/HeroBar'),
    CardComponent: () => import('@/components/CardComponent'),
    CarList: () => import('@/components/Board/CarList'),
    DriverList: () => import('@/components/Board/DriverList'),
    CarDriverList: () => import('@/components/Board/CarDriverList'),
    TransfersTable: () => import(/* webpackPrefetch: true */ '@/components/TransfersTable'),
    TransferDetails: () => import(/* webpackPrefetch: true */ '@/views/TransferDetails'),
  },
  data () {
    return {
      refreshLoading: true,
      pageLoading: false,
      isTransferListModalActive: false,
      isTransferModalActive: false,
      transferId: null,
      driverId: null,
      carId: null,
      board: {
        availableVehicles: [],
        availableDrivers: [],
        waitingForDepartureTransfers: [],
        enRouteTransfers: [],
        completedTransfers: [],
      },
      excelLoading: false,
    }
  },
  computed: {
    ...mapState([
      'boardRefresh',
    ]),
    transferListWhere () {
      return {
        dateRange: [
          moment().toDate(), moment().toDate()
        ],
        status: [1]
      }
    }
  },
  watch: {
    boardRefresh (n) {
      if (n == true) {
        this.refresh()
      }
    }
  },
  created () {
  },
  mounted () {
    this.pageLoading = true
    setTimeout(() => {
      this.loadBoard();
    }, 700);
  },
  methods: {
    loadBoard () {
      this.$http
        .get('board')
        .then(({ data }) => {
          this.board = data;
          this.refreshLoading = false
          this.pageLoading = false
          this.$store.commit('boardRefresh', false)
        })
        .finally(() => {
          this.hideSpinner()
        });
    },
    showTransferList (driverId, carId) {
      this.driverId = driverId;
      this.carId = carId;
      this.isTransferListModalActive = true;
    },
    showTransfer (transferId) {
      this.transferId = transferId;
      this.isTransferModalActive = true;
    },
    refresh () {
      document.getElementById('refresh-button').blur();
      this.refreshLoading = true;
      this.loadBoard()
    },
    getCount (key) {
      return this.pageLoading ? '...' : this.board[key].length
    },
    exportExcel () {
      this.excelLoading = true;

      this.$http({
        url: '/board?excel=1',
        method: 'GET',
        responseType: 'blob',
      })
        .then(response => {
          const mimType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          const fileName = `export-pano-${moment().format('DD-MM-YYYY-h-mm')}.xlsx`
          download(response.data, fileName, mimType)
          this.excelLoading = false;
          this.excel = false;
          return false;
        })
        .catch(e => {
          const errors = this.parseError(e);
          this.$buefy.toast.open({
            message: 'İşlem sırasında hata oluştu:<br>' + errors,
            type: 'is-danger',
            duration: 5000
          });
        })
        .finally(() => {
          this.excelLoading = false;
          this.isLoading = false;
        })
    },
  },
}
</script>
